import React from "react";

import { FormattedMessage } from "react-intl";

import Api from "src/services/api";

import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import ClearIcon from '@mui/icons-material/Clear';
import Button from "@mui/material/Button";
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

import PasswordPolicy from "../passwordPolicy";

const defaultState = {
  loading: false,
  password: "",
  confirmPassword: "",
  configPasswordPolicy: null,
  showConfirmDialog: false
};

export default function UserUpdatePassword(props) {
  const {
    open,
    handleOpenOrClose,
    selectedUserToUpdatePassword,
    intl,
    classes,
    setState: globalSetState,
  } = props;

  const [internalModalState, setInternalModalState] = React.useState(defaultState);

  const name = selectedUserToUpdatePassword != null ? selectedUserToUpdatePassword.name : "";
  const userToUpdateId = selectedUserToUpdatePassword != null ? selectedUserToUpdatePassword.id : null;

  function handleCancelConfirmation() {
    setInternalModalState(prev => ({ ...prev, showConfirmDialog: false, loading: false }));
  }

  function validatePassword() {
    if (!userToUpdateId || userToUpdateId == null) {
      globalSetState({
        openNotification: true,
        notificationVariant: "error",
        notificationMessage: intl.formatMessage({ id: "process.error" })
      });
      return false;
    }

    if (internalModalState.password !== internalModalState.confirmPassword) {
      globalSetState({
        openNotification: true,
        notificationVariant: "error",
        notificationMessage: intl.formatMessage({ id: "password.reset.passwords.dont.match" })
      });
      return false;
    } else if (internalModalState.configPasswordPolicy != null && document.querySelector('[data-testid="CloseIcon"]')) {
      globalSetState({
        openNotification: true,
        notificationVariant: "error",
        notificationMessage: intl.formatMessage({ id: "passwordpolicy.errorPolicy" }),
      });
      return false;

    } else {
      return true;
    }
  }

  function handleSubmit(e) {
    e.preventDefault();

    if (!validatePassword()) return;

    setInternalModalState(prev => ({ ...prev, loading: true, showConfirmDialog: true }));
  }

  function handleConfirmPasswordChange() {
    setInternalModalState(prev => ({ ...prev, showConfirmDialog: false, loading: false }));

    globalSetState({ loading: true });

    const { password, confirmPassword } = internalModalState;

    Api.post("/users/global-adm-change-password-auth", { newPassword: password, newPasswordConfirm: confirmPassword ,userToUpdateId })
      .then(result => {
        if (result.data.success) {
          globalSetState({
            loading: false,
            openNotification: true,
            notificationVariant: "success",
            notificationMessage: props.intl.formatMessage({ id: "password.update.password.registered" })
          });

          handleOpenOrClose(null);
        }
      })
      .catch(err => {
        globalSetState({
          loading: false,
          openNotification: true,
          notificationVariant: "error",
          notificationMessage: err.response && err.response.data && err.response.data.errors ? err.response.data.errors[0] : props.intl.formatMessage({ id: "process.error" })
        });
      });
  }

  async function getConfigPasswordPolicy() {
    let url = `/configs/configpasswordpolicy`;

    Api.get(url)
      .then(result => {
        if (result.data.success) {
          const data = result.data.data;

          setInternalModalState(prevState => ({ ...prevState, configPasswordPolicy: data }));
        }
        else {
          globalSetState({
            openNotification: true, notificationVariant: 'error',
            notificationMessage: result.data && result.data.response && result.data.response.data && result.data.response.data.errors && result.data.response.data.errors[0] ? result.data.response.data.errors[0] : intl.formatMessage({ id: "process.error" })
          });

          Api.kickoff(result);
        }
      })
      .catch(err => {
        globalSetState({
          openNotification: true, notificationVariant: 'error',
          notificationMessage: err.response && err.response.data && err.response.data.errors && err.response.data.errors[0] ? err.response.data.errors[0] : intl.formatMessage({ id: "process.error" })
        });

        Api.kickoff(err);
      });
  }

  React.useEffect(() => {
    if (!open) return;

    let request = false;

    const { configPasswordPolicy } = internalModalState;

    setInternalModalState({ ...defaultState, configPasswordPolicy, loading: true });

    async function fetchHistory() {
      if (request || !!configPasswordPolicy) return;

      await getConfigPasswordPolicy();
    }

    fetchHistory();

    setInternalModalState(prevState => ({ ...prevState, loading: false }));

    return () => { request = true; };
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={() => handleOpenOrClose(null)}
        aria-labelledby="newuser-dialog-title"
        scroll="paper"
        maxWidth="md"
        fullWidth
        sx={{ transition: "all 0.3s ease" }}
      >
        <form className={classes.form} onSubmit={handleSubmit}>
          <DialogTitle id="update-password-dialog-title">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6" fontWeight="bold"><FormattedMessage id="user.update.password" /></Typography>
              <IconButton aria-label="close" size="small" onClick={() => handleOpenOrClose(null)}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent
            sx={{
              position: 'relative',
              p: 0,
              minHeight: '200px',
            }}
          >
            {internalModalState.loading &&
              <Grid
                item
                xs={12}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: '100%',
                  width: '100%',
                  position: 'absolute',
                  backgroundColor: "rgba(255, 255, 255, 0.7)",
                  zIndex: 2
                }}
              >
                <CircularProgress color="primary" />
              </Grid>
            }

            <Box sx={{
              marginY: 2,
              marginX: 3,
            }}>
              <Typography variant="h5">
                <FormattedMessage
                  id="user.update.password.description"
                  values={{
                    attention: <strong>{intl.formatMessage({ id: "attention" })}</strong>,
                    user: <strong>'{name}'</strong>
                  }}
                />
              </Typography>

              <Grid container spacing={2} pt={2}>
                <Grid item xs={12}>
                  <TextField
                    variant="outlined"
                    required
                    fullWidth
                    name="password"
                    label={<FormattedMessage id="password" />}
                    type="password"
                    id="password"
                    autoComplete="current-password"
                    value={internalModalState.password}
                    onChange={e => setInternalModalState(prev => ({ ...prev, password: e.target.value }))}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    variant="outlined"
                    required
                    fullWidth
                    name="confirmPassword"
                    label={<FormattedMessage id="confirm.password" />}
                    type="password"
                    id="confirmPassword"
                    autoComplete="current-confirmPassword"
                    value={internalModalState.confirmPassword}
                    onChange={e => setInternalModalState(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  />
                </Grid>

                <Grid item xs={12}>
                  <PasswordPolicy
                    password={internalModalState.password}
                    configPasswordPolicy={internalModalState.configPasswordPolicy}
                  />
                </Grid>
              </Grid>
            </Box>

          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant='outlined'
                color='primary'
                type="button"
                onClick={() => handleOpenOrClose(null)}
                sx={{ minWidth: 120 }}
                disabled={internalModalState.loading}
              >
                <FormattedMessage id="close" />
              </Button>

              <Button
                variant='contained'
                color='primary'
                type="submit"
                sx={{ minWidth: 120 }}
                disabled={internalModalState.loading}
              >
                <FormattedMessage id="save" />
              </Button>
            </Box>
          </DialogActions>
        </form>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={internalModalState.showConfirmDialog}
        onClose={handleCancelConfirmation}
        aria-labelledby="confirm-password-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="confirm-password-dialog-title">
          <FormattedMessage id="confirm" />
        </DialogTitle>
        <DialogContent>
          <Typography>
            <FormattedMessage
              id="user.update.password.confirmation"
              defaultMessage="user.update.password.confirmation"
              values={{
                user: <><br /><strong>'{name}'</strong></>
              }}
            />
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant='outlined'
              color='primary'
              onClick={handleCancelConfirmation}
              sx={{ minWidth: 100 }}
            >
              <FormattedMessage id="cancel" />
            </Button>
            <Button
              variant='contained'
              color='primary'
              onClick={handleConfirmPasswordChange}
              sx={{ minWidth: 100 }}
            >
              <FormattedMessage id="confirm" />
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
}
